import React, { useState, useEffect } from "react";
import { get, post } from "../../../services/api";
import { toast } from "react-toastify";
import NotificationEventTypes from "../../../constants/notificationEventsEnum";
import { useForm, useFieldArray } from "react-hook-form";
import { createPortal } from "react-dom";
import DropdownWithCustomValuesModal from "../../../components/DropdownWithCustomValuesModal";
const Siblings = ({ selectedStudent }) => {
  const token = localStorage.getItem("token");
  const roleId = localStorage.getItem("roleId");
  const organizationId = localStorage.getItem("organizationId");
  const centerId = localStorage.getItem("centerId");
  const userId = selectedStudent.userId;
  const {
    register,
    watch,
    control,
    handleSubmit,
    setValue,
    getValues,
    reset,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm({
    shouldUnregister: true,
    defaultValues: {
      userId: userId,
      siblingDtos: [
        {
          id: 0,
          name: "",
          gender: "",
          dob: null,
          isSchoolGoing: "",
          schoolName: "",
          class: "",
          mediumOfStudy: "",
        },
      ],
    },
  });

  const {
    fields: siblingDtos,
    append: appendOtherSiblingsField,
    remove: removeOtherSiblingsField,
    replace,
  } = useFieldArray({
    control,
    name: "siblingDtos",
  });

  const [pupils, setPupils] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPupils, setIsLoadingPupils] = useState(false);
  const [isLoadingSiblings, setIsLoadingSiblings] = useState(false);

  useEffect(() => {
    const fetchPupils = async () => {
      if (!userId) {
        console.error("No valid userId available");
        return;
      }
      setIsLoadingPupils(false);
      try {
        const response = await get(
          `Student/GetPupilsInformationByUserId?userId=${userId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setPupils(response);
      } catch (error) {
        console.error("Error fetching pupils:", error);
        toast.error("Failed to fetch pupils.");
      } finally {
        setIsLoadingPupils(false);
      }
    };

    const fetchSiblings = async () => {
      if (!userId) {
        console.error("No valid userId available for siblings");
        return;
      }
      setIsLoadingSiblings(false);
      try {
        const response = await get(
          `Student/GetSiblingInformation?UserId=${userId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (response && response.length > 0) {
          const formattedSiblings = response.map((sibling) => ({
            id: sibling.id || 0,
            name: sibling.name || "",
            gender: sibling.gender?.toString() || "",
            dob: sibling.dob ? sibling.dob.split("T")[0] : null,
            isSchoolGoing:
              sibling.isSchoolGoing === true
                ? "Yes"
                : sibling.isSchoolGoing === false
                ? "No"
                : "",
            schoolName: sibling.schoolName || "",
            class: sibling.class || "",
            mediumOfStudy: sibling.mediumOfStudy || "",
            parentId: sibling.parentId,
          }));
          replace(formattedSiblings);
        } else {
          replace([
            {
              id: 0,
              name: "",
              gender: "",
              dob: null,
              isSchoolGoing: "",
              schoolName: "",
              class: "",
              mediumOfStudy: "",
            },
          ]);
        }
      } catch (error) {
        console.error("Error fetching siblings:", error);
        toast.error("Failed to fetch sibling information.");
      } finally {
        setIsLoadingSiblings(false);
      }
    };

    fetchPupils();
    fetchSiblings();
  }, []);

  const onSaveForm = async () => {
    if (!token) {
      toast.error("Unauthorized. Please log in.");
      return false;
    }

    const isValid = await trigger("siblingDtos");
    if (!isValid) {
      // toast.error("Please fix validation errors before submitting.");
      return false;
    }

    const formValues = getValues();

    // Filter out completely empty rows
    const filledSiblings = formValues.siblingDtos.filter((sibling) => {
      return (
        sibling.name ||
        sibling.gender !== "" ||
        sibling.dob ||
        sibling.isSchoolGoing !== "" ||
        sibling.schoolName ||
        sibling.class ||
        sibling.mediumOfStudy
      );
    });

    const formData = {
      userId: userId || 0,
      siblingDtos: filledSiblings.map((sibling) => ({
        id: sibling.id || 0,
        name: sibling.name || "",
        gender:
          sibling.gender !== "" && sibling.gender !== undefined
            ? parseInt(sibling.gender)
            : null,
        dob: sibling.dob?.split("T")[0] || null,
        isSchoolGoing:
          sibling.isSchoolGoing === "Yes"
            ? true
            : sibling.isSchoolGoing === "No"
            ? false
            : null,
        schoolName: sibling.schoolName || "",
        class: sibling.class || "",
        mediumOfStudy: sibling.mediumOfStudy || "",
      })),
    };

    try {
      const response = await post("Student/AddSiblingInformation", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.isSuccess) {
        toast.success(response.message);
        return true;
      } else {
        toast.error(response.message || "An unexpected error occurred");
        return false;
      }
    } catch (error) {
      console.error("Error submitting sibling information:", error);
      toast.error("Failed to save sibling information.");
      return false;
    }
  };

  return (
    <div className="profile-content-area">
      <div className="mb-5">
        <h5 className="mb-0 fw-bold">Siblings</h5>
      </div>
      <div className="profile-scrollable-area p-1">
        {/* <h5 className="mb-2 p-2 px-3 text-secondary bg-brand-light rounded-pill">
          Added Pupils
        </h5> */}

        <div className="row d-none">
          <div className="col-md-12">
            {isLoadingPupils ? (
              <div className="text-center">
                <span className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </span>
              </div>
            ) : (
              <table className="table custom-table table-bordered">
                <thead>
                  <tr>
                    <th>First Name</th>
                    <th>Last Name</th>
                    <th>Gender</th>
                    <th>DOB</th>
                    <th>Student Code</th>
                  </tr>
                </thead>
                <tbody>
                  {pupils.map((item, index) => (
                    <tr>
                      <td>{item.firstName}</td>
                      <td>{item.lastName}</td>
                      <td>{item.gender === 1 ? "Male" : "Female"}</td>
                      <td>{item.greGorianDOB.split("T")[0]}</td>
                      <td>{item.code}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <h5 className="mb-3 p-2 px-3 text-secondary bg-brand-light rounded-pill">
          Other Siblings (If any)
        </h5>

        {isLoadingSiblings ? (
          <div className="text-center">
            <span className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </span>
          </div>
        ) : (
          <>
            <div className="row g-2">
              <div className="col">
                <label className="form-label">Sibling Name</label>
              </div>
              <div className="col">
                <label className="form-label">Gender</label>
              </div>
              <div className="col">
                <label className="form-label">Date of Birth</label>
              </div>
              <div className="col">
                <label className="form-label">School Going</label>
              </div>
              <div className="col">
                <label className="form-label">School Name</label>
              </div>
              <div className="col">
                <label className="form-label">Class/Grade</label>
              </div>
              <div className="col">
                <label className="form-label">Medium of Study</label>
              </div>
              <div className="col-1">
                <label className="form-label">Action</label>
              </div>
            </div>

            {siblingDtos?.map((field, index) => (
              <div className="row g-2 mb-2" key={field.id}>
                <div className="col">
                  <input
                    type="text"
                    {...register(`siblingDtos.${index}.name`, {
                      validate: (value, formValues) => {
                        const sibling = getValues(`siblingDtos.${index}`);
                        const hasOtherFieldsFilled =
                          sibling.gender !== "" ||
                          sibling.dob ||
                          sibling.isSchoolGoing !== "" ||
                          sibling.schoolName ||
                          sibling.class ||
                          sibling.mediumOfStudy;

                        if (hasOtherFieldsFilled && !value?.trim()) {
                          return "This field is required.";
                        }
                        return true;
                      },
                    })}
                    className="form-control"
                  />
                  {errors.siblingDtos?.[index]?.name && (
                    <small className="text-danger">
                      {errors.siblingDtos[index].name.message}
                    </small>
                  )}
                </div>
                <div className="col">
                  <select
                    {...register(`siblingDtos.${index}.gender`, {})}
                    className="form-select"
                  >
                    <option value="">Select</option>
                    <option value="1">Male</option>
                    <option value="0">Female</option>
                  </select>
                  {errors.siblingDtos?.[index]?.gender && (
                    <small className="text-danger">
                      {errors.siblingDtos[index].gender.message}
                    </small>
                  )}
                </div>
                <div className="col">
                  <input
                    type="date"
                    {...register(`siblingDtos.${index}.dob`, {})}
                    className="form-control"
                  />
                  {errors.siblingDtos?.[index]?.dob && (
                    <small className="text-danger">
                      {errors.siblingDtos[index].dob.message}
                    </small>
                  )}
                </div>
                <div className="col">
                  <select
                    {...register(`siblingDtos.${index}.isSchoolGoing`, {})}
                    className="form-select"
                  >
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                  {errors.siblingDtos?.[index]?.isSchoolGoing && (
                    <small className="text-danger">
                      {errors.siblingDtos[index].isSchoolGoing.message}
                    </small>
                  )}
                </div>
                <div className="col">
                  <input
                    type="text"
                    {...register(`siblingDtos.${index}.schoolName`, {})}
                    className="form-control"
                  />
                  {errors.siblingDtos?.[index]?.schoolName && (
                    <small className="text-danger">
                      {errors.siblingDtos[index].schoolName.message}
                    </small>
                  )}
                </div>
                <div className="col">
                  <input
                    type="text"
                    {...register(`siblingDtos.${index}.class`, {})}
                    className="form-control"
                  />
                  {errors.siblingDtos?.[index]?.class && (
                    <small className="text-danger">
                      {errors.siblingDtos[index].class.message}
                    </small>
                  )}
                </div>
                <div className="col">
                  <input
                    type="text"
                    {...register(`siblingDtos.${index}.mediumOfStudy`, {})}
                    className="form-control"
                  />
                  {errors.siblingDtos?.[index]?.mediumOfStudy && (
                    <small className="text-danger">
                      {errors.siblingDtos[index].mediumOfStudy.message}
                    </small>
                  )}
                </div>
                <div className="col-1">
                  <button
                    className="btn btn-outline-primary py-1 px-2 lh-2"
                    type="button"
                    onClick={() =>
                      appendOtherSiblingsField({
                        userId: userId,
                        siblingDtos: [
                          {
                            id: 0,
                            name: "",
                            gender: "",
                            dob: null,
                            isSchoolGoing: "",
                            schoolName: "",
                            class: "",
                            mediumOfStudy: "",
                          },
                        ],
                      })
                    }
                  >
                    <span className="fas fa-plus"></span>
                  </button>
                  {siblingDtos.length > 1 && (
                    <button
                      className="btn btn-outline-danger py-1 px-2 lh-2 ms-1"
                      type="button"
                      onClick={() => removeOtherSiblingsField(index)}
                    >
                      <span className="fas fa-minus"></span>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </>
        )}
      </div>
      <div className="mt-auto">
        <div className="d-flex justify-content-end gap-3">
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={onSaveForm}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default Siblings;
